/* Release sitting (13-12). Built to design/mock-13 (Release tab): .pagehead, .filters, .tsheet with
   .tscroll (sticky head), the sticky glass .composer, and dialog.confirm with .progress / .result /
   .exlist. Shared vocabulary copied from the Verify page (13-11) so each page owns its module.
   Purple is identity only; Release is teal, Retire is charcoal (the mock's .solemn); age and past-due
   are a gentle amber, never red (D-13-02-1). */

.page {
  max-width: var(--shell-max);
  margin: 0 auto;
  padding: 0 var(--shell-gutter) 120px;
}

/* ---------- page head ---------- */
.head {
  padding: 50px 0 28px;
  display: flex;
  align-items: flex-end;
  gap: 24px;
  flex-wrap: wrap;
}

.title {
  font-family: var(--serif);
  font-weight: 400;
  font-size: 40px;
  line-height: 1.1;
  color: var(--purple-dark);
  letter-spacing: -0.01em;
}

.lede {
  margin-top: 8px;
  color: var(--gray);
  max-width: 66ch;
}

.side {
  margin-left: auto;
  font-size: 13.5px;
  color: var(--gray);
  text-align: right;
}

.side b {
  display: block;
  font-family: var(--serif);
  font-weight: 500;
  font-size: 30px;
  color: var(--charcoal);
  line-height: 1.1;
  font-variant-numeric: tabular-nums;
}

/* ---------- filters ---------- */
.filters {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 14px;
  margin-bottom: 16px;
}

.filter {
  display: flex;
  flex-direction: column;
  gap: 5px;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--gray);
}

.filter select {
  height: 38px;
  min-width: 140px;
  max-width: 220px;
  border: 1px solid var(--sage-dark);
  border-radius: 10px;
  background: var(--white)
    url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236B7280' stroke-width='2.5'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")
    no-repeat right 12px center;
  padding: 0 34px 0 12px;
  font: inherit;
  font-size: 14px;
  font-weight: 500;
  color: var(--charcoal);
  appearance: none;
  cursor: pointer;
}

.filter select:focus-visible {
  outline: none;
  border-color: var(--purple);
  box-shadow: 0 0 0 3px rgba(127, 63, 150, 0.1);
}

.showing {
  margin-left: auto;
  font-size: 13.5px;
  color: var(--gray);
  padding-bottom: 9px;
}

/* ---------- the table ---------- */
.tsheet {
  background: var(--white);
  border: 1px solid var(--line);
  border-radius: 18px;
  box-shadow: var(--shadow-sm);
  /* The mock's .tscroll: the table scrolls inside its card, under a sticky head, so the
     composer below it stays in view. */
  max-height: max(380px, calc(100vh - 470px));
  overflow: auto;
  /* The containing block for the visually hidden inputs / labels, so they scroll with the
     table instead of widening the page at phone width. */
  position: relative;
}

.table thead th {
  position: sticky;
  top: 0;
  z-index: 2;
}

.table tbody td {
  padding-top: 11px;
  padding-bottom: 11px;
}

.table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.table th {
  font-size: 12.5px;
  font-weight: 600;
  color: var(--gray);
  text-align: left;
  padding: 12px 14px;
  border-bottom: 1px solid var(--sage-dark);
  background: var(--white);
  white-space: nowrap;
}

.table td {
  padding: 14px;
  border-bottom: 1px solid var(--line);
  vertical-align: top;
}

.table tbody tr:last-child td {
  border-bottom: none;
}

.row {
  transition: opacity 0.3s ease;
  outline: none;
}

.row[data-hovered] td {
  background: #fbfdfc;
}

.row[data-selected] td {
  background: var(--lavender-light);
}

.row[data-focus-visible] td {
  box-shadow: inset 0 2px 0 var(--purple), inset 0 -2px 0 var(--purple);
}

.row[data-focus-visible] td:first-child {
  box-shadow: inset 2px 2px 0 var(--purple), inset 0 -2px 0 var(--purple);
}

.row[data-focus-visible] td:last-child {
  box-shadow: inset -2px 2px 0 var(--purple), inset 0 -2px 0 var(--purple);
}

.leaving {
  opacity: 0;
}

.ck {
  width: 44px;
  padding-left: 18px !important;
  padding-right: 4px !important;
}

.colAge {
  width: 72px;
}

.num {
  text-align: right !important;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.old {
  color: var(--amber);
  font-weight: 600;
}

.acts {
  white-space: nowrap;
  text-align: right !important;
}

.mono {
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.sub {
  font-size: 12.5px;
  color: var(--gray);
  margin-top: 3px;
}

.sr {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}

.chev {
  width: 30px;
  height: 30px;
  margin-left: 4px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  vertical-align: middle;
  color: var(--gray);
  background: none;
  border: none;
  cursor: pointer;
}

.chev svg {
  width: 16px;
  height: 16px;
  fill: none;
  stroke: currentColor;
  stroke-width: 2.2;
  transition: transform 0.2s var(--ease);
}

.chev[data-hovered] {
  background: var(--lavender-light);
  color: var(--purple-dark);
}

.chev[data-focus-visible] {
  outline: 2px solid var(--purple);
  outline-offset: 2px;
}

.chevOpen {
  transform: rotate(180deg);
}

/* checkboxes (RAC Checkbox, slot="selection") */
.check {
  display: inline-flex;
  cursor: pointer;
}

.check[data-disabled] {
  cursor: not-allowed;
}

.box {
  width: 17px;
  height: 17px;
  border-radius: 4px;
  border: 1.5px solid var(--gray-light);
  background: var(--white);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  vertical-align: middle;
}

.box[data-on] {
  background: var(--teal);
  border-color: var(--teal);
}

.box[data-off] {
  opacity: 0.35;
}

.box svg {
  width: 12px;
  height: 12px;
  fill: none;
  stroke: #fff;
  stroke-width: 2.4;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.check[data-focus-visible] .box {
  outline: 2px solid var(--purple);
  outline-offset: 2px;
}

/* the expanded row */
.detailRow td {
  background: var(--bg);
}

.details {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 18px 32px;
  margin-top: 4px;
}

.facts {
  display: grid;
  grid-template-columns: 130px minmax(0, 1fr);
  gap: 6px 14px;
  font-size: 13.5px;
  align-content: start;
}

.facts dt {
  color: var(--gray);
}

.facts dd {
  min-width: 0;
}

/* empty / notice */
.empty {
  padding: 64px 24px;
  text-align: center;
  color: var(--gray);
}

.empty h2,
.notice h2 {
  font-family: var(--serif);
  font-weight: 400;
  font-size: 28px;
  color: var(--purple-dark);
  margin-bottom: 6px;
}

.empty .btn {
  margin-top: 14px;
}

.notice {
  background: var(--white);
  border: 1px solid var(--line);
  border-radius: 18px;
  padding: 40px 32px;
  max-width: 640px;
  color: var(--gray);
}

/* ---------- buttons (the mock's .btn) ---------- */
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 36px;
  padding: 0 18px;
  border-radius: 999px;
  font: inherit;
  font-size: 13.5px;
  font-weight: 600;
  border: 1px solid transparent;
  background: none;
  color: inherit;
  white-space: nowrap;
  cursor: pointer;
  vertical-align: middle;
  transition: background 0.2s, border-color 0.2s, color 0.2s, transform 0.12s;
}

.btn[data-pressed]:not([data-disabled]) {
  transform: scale(0.97);
}

.btn[data-focus-visible] {
  outline: 2px solid var(--purple);
  outline-offset: 2px;
}

.btn[data-disabled] {
  opacity: 0.38;
  cursor: not-allowed;
}

.go {
  background: var(--teal);
  color: #fff;
}

.go[data-hovered]:not([data-disabled]) {
  background: var(--teal-dark);
}

.line {
  background: var(--white);
  border-color: var(--sage-dark);
  color: var(--charcoal);
}

.line[data-hovered]:not([data-disabled]) {
  border-color: var(--purple-light);
  color: var(--purple-dark);
}

.quiet {
  color: var(--gray);
  padding: 0 12px;
}

.quiet[data-hovered]:not([data-disabled]) {
  background: var(--lavender-light);
  color: var(--purple-dark);
}

.sm {
  height: 30px;
  padding: 0 13px;
  font-size: 12.8px;
}

/* ---------- confirm dialog (the mock's dialog.confirm) ---------- */
.overlay {
  position: fixed;
  inset: 0;
  z-index: 200;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(45, 45, 45, 0.3);
  backdrop-filter: blur(2px);
  padding: 16px;
}

.overlay[data-entering] {
  animation: fade 0.2s ease;
}

.modal {
  width: min(480px, 100%);
  max-height: calc(100vh - 32px);
  overflow: auto;
  background: var(--white);
  border-radius: 20px;
  box-shadow: 0 30px 70px rgba(45, 45, 45, 0.25);
}

.modal[data-entering] {
  animation: popIn 0.26s var(--ease);
}

@keyframes fade {
  from {
    opacity: 0;
  }
}

@keyframes popIn {
  from {
    opacity: 0;
    transform: scale(0.97) translateY(6px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}

.dialog {
  outline: none;
}

.cb {
  padding: 26px 28px 8px;
}

.cb h2 {
  font-family: var(--serif);
  font-weight: 400;
  font-size: 26px;
  line-height: 1.2;
  color: var(--purple-dark);
  margin-bottom: 10px;
}

.cb p {
  font-size: 14.5px;
  color: var(--charcoal);
  margin-bottom: 10px;
}

.cb .muted,
.muted {
  color: var(--gray);
  font-size: 13.5px;
}

.breakdown {
  list-style: none;
  margin: 4px 0 12px;
  font-size: 14px;
}

.breakdown li {
  padding: 6px 0;
  border-top: 1px solid var(--line);
}

.breakdown li:first-child {
  border-top: none;
}

.breakdown b {
  display: inline-block;
  min-width: 34px;
  font-family: var(--serif);
  font-weight: 500;
  font-size: 17px;
  font-variant-numeric: tabular-nums;
}

.cf {
  padding: 16px 28px 22px;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.result {
  font-family: var(--serif);
  font-size: 21px !important;
  line-height: 1.35;
  color: var(--charcoal);
  margin: 4px 0 14px;
}

.exlist {
  list-style: none;
  font-size: 13.5px;
  margin-bottom: 8px;
}

.exlist li {
  padding: 7px 0;
  border-top: 1px solid var(--line);
  display: flex;
  gap: 10px;
}

.exlist li .mono {
  flex: none;
}

/* ---------- release sitting additions ---------- */
.sideNote {
  display: block;
  margin-top: 4px;
  font-size: 12.5px;
  color: var(--gray);
}

/* source facets + batch clusters */
.chips {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.chipsLabel {
  font-size: 12.5px;
  font-weight: 600;
  color: var(--gray);
  min-width: 62px;
}

.chip {
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  height: 32px;
  padding: 0 13px;
  border-radius: 999px;
  border: 1px solid var(--sage-dark);
  background: var(--white);
  font: inherit;
  font-size: 13px;
  font-weight: 500;
  color: var(--charcoal);
  line-height: 30px;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s, color 0.2s;
}

.chip b {
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: var(--gray);
}

.chip:hover {
  border-color: var(--purple-light);
  color: var(--purple-dark);
}

.chip:focus-visible {
  outline: 2px solid var(--purple);
  outline-offset: 2px;
}

.chip[aria-pressed='true'] {
  background: var(--lavender-light);
  border-color: var(--purple-light);
  color: var(--purple-dark);
}

.chip[aria-pressed='true'] b {
  color: var(--purple-dark);
}

.cluster {
  background: var(--sage-light);
  border-color: transparent;
}

.chipSub {
  font-size: 12px;
  color: var(--gray);
}

/* search + age slider */
.search input {
  height: 38px;
  width: 210px;
  border: 1px solid var(--sage-dark);
  border-radius: 10px;
  background: var(--white);
  padding: 0 12px;
  font: inherit;
  font-size: 14px;
  font-weight: 500;
  color: var(--charcoal);
}

.search input:focus-visible {
  outline: none;
  border-color: var(--purple);
  box-shadow: 0 0 0 3px rgba(127, 63, 150, 0.1);
}

.age {
  min-width: 170px;
}

.age output {
  font-weight: 500;
  color: var(--charcoal);
}

.age input[type='range'] {
  height: 38px;
  width: 100%;
  accent-color: var(--teal);
  cursor: pointer;
}

.linkbtn {
  border: none;
  background: none;
  padding: 0;
  font: inherit;
  font-weight: 600;
  color: var(--teal-dark);
  cursor: pointer;
  text-decoration: underline;
  text-decoration-color: var(--sage-dark);
  text-underline-offset: 3px;
}

.linkbtn:hover {
  color: var(--purple-dark);
}

.practiceNote {
  margin: -4px 0 12px;
  font-size: 13px;
  color: var(--gray);
}

/* table columns */
.colRef {
  width: 92px;
}

.colTitle {
  min-width: 260px;
}

.colKind {
  width: 84px;
}

.colSource {
  width: 128px;
}

.colRole {
  width: 118px;
}

.colDue {
  width: 74px;
}

.colBy {
  width: 72px;
}

.none {
  color: var(--gray-light);
}

/* not releasable (ISS-187), regulatory, last-run outcome: said, never hidden */
.heldRow td {
  color: var(--gray);
}

.heldTag,
.markTag,
.regBadge {
  display: inline-block;
  margin-left: 8px;
  font-size: 11.5px;
  font-weight: 600;
  padding: 2px 9px;
  border-radius: 999px;
  white-space: nowrap;
  vertical-align: 1px;
}

.heldTag {
  background: var(--sage-light);
  color: var(--gray);
}

.markTag {
  background: var(--amber-soft);
  color: var(--amber);
}

.regBadge {
  background: var(--charcoal);
  color: #fff;
}

.detailHead {
  font-family: var(--serif);
  font-weight: 500;
  font-size: 16px;
  color: var(--charcoal);
  margin: 0 0 4px;
}

.prose {
  font-size: 13.8px;
  max-width: 64ch;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  margin-bottom: 12px;
}

/* the sticky glass composer (the mock's .composer) */
.composer {
  position: sticky;
  bottom: 0;
  z-index: 30;
  margin-top: 18px;
  background: rgba(255, 255, 255, 0.94);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid var(--line);
  border-radius: 18px;
  box-shadow: var(--shadow-md);
  padding: 16px 18px;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  gap: 16px;
  align-items: center;
  animation: composerIn 0.28s var(--ease);
}

.composer[hidden] {
  display: none;
}

@keyframes composerIn {
  from {
    opacity: 0;
    transform: translateY(14px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}

.cnt {
  font-family: var(--serif);
  font-size: 26px;
  line-height: 1;
  color: var(--charcoal);
  font-variant-numeric: tabular-nums;
  min-width: 62px;
}

.cnt small {
  display: block;
  font-family: var(--sans);
  font-size: 12px;
  color: var(--gray);
  margin-top: 4px;
}

.noteField {
  min-width: 0;
}

.noteInput {
  display: block;
  width: 100%;
  min-height: 44px;
  height: 44px;
  resize: vertical;
  border: 1px solid var(--sage-dark);
  border-radius: 10px;
  background: var(--white);
  padding: 10px 13px;
  font: inherit;
  font-size: 14px;
  line-height: 1.5;
  color: var(--charcoal);
}

.noteInput:focus {
  outline: none;
  border-color: var(--purple);
  box-shadow: 0 0 0 3px rgba(127, 63, 150, 0.1);
}

.noteHint {
  margin-top: 6px;
  font-size: 12.5px;
  color: var(--gray);
}

.btns {
  display: flex;
  gap: 8px;
}

.solemn {
  background: var(--charcoal);
  color: #fff;
}

.solemn[data-hovered]:not([data-disabled]) {
  background: #000;
}

/* confirm: the note as it will appear, and the warnings */
.quoteLabel {
  margin-top: 6px;
}

.quote {
  border-left: 3px solid var(--sage-dark);
  padding: 6px 0 6px 14px;
  margin: 6px 0 14px;
  font-family: var(--serif);
  font-style: italic;
  font-size: 16px;
  color: var(--charcoal);
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  max-height: 180px;
  overflow: auto;
}

.warn {
  font-size: 13.5px !important;
  color: var(--amber) !important;
  background: var(--amber-soft);
  border: 1px solid var(--amber-line);
  border-radius: 10px;
  padding: 8px 12px;
}

/* ISS-212: the note names the other verb. The question leads, in the gentle amber (never red). */
.guard {
  background: var(--amber-soft);
  border: 1px solid var(--amber-line);
  border-radius: 14px;
  padding: 14px 16px 4px;
  margin-bottom: 16px;
}

.cb .guard h2 {
  font-size: 22px;
  color: var(--amber-dark);
  margin-bottom: 6px;
}

.cb .guard p {
  font-size: 14px;
}

.guardSub {
  font-size: 15px;
  font-weight: 600;
  color: var(--charcoal);
  margin: 0 0 8px;
}

/* the determinate progress bar (per chunk) */
.bar {
  height: 6px;
  border-radius: 999px;
  background: var(--sage-light);
  overflow: hidden;
  margin: 14px 0 8px;
}

.bar i {
  display: block;
  height: 100%;
  width: 0;
  background: var(--teal);
  border-radius: 999px;
  transition: width 0.24s ease;
}

.exlist li:first-child {
  border-top: none;
}

/* ---------- show the next page ---------- */
.more {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  margin-top: 18px;
  font-size: 13.5px;
  color: var(--gray);
}

/* ---------- responsive (the mock's 1100 / 720 px steps) ---------- */
@media (max-width: 1100px) {
  .table td,
  .table th {
    padding-left: 10px;
    padding-right: 10px;
  }
}

@media (max-width: 960px) {
  .details {
    grid-template-columns: minmax(0, 1fr);
  }
}

@media (max-width: 720px) {
  .tsheet {
    overflow-x: auto;
  }

  .filters {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  }

  .filter select {
    min-width: 0;
    max-width: none;
    width: 100%;
  }

  .showing {
    grid-column: 1 / -1;
    margin-left: 0;
    padding-bottom: 0;
  }

  .search,
  .age {
    grid-column: 1 / -1;
  }

  .search input {
    width: 100%;
  }

  .chipsLabel {
    width: 100%;
  }

  .table {
    min-width: 980px;
  }
}

@media (max-width: 860px) {
  .composer {
    grid-template-columns: auto minmax(0, 1fr);
  }

  .btns {
    grid-column: 1 / 3;
    justify-content: flex-end;
  }
}
